import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal } from '@components/ui';
import { CTA } from '@components/ui/designTokens';
import { apiClient } from '@lib/api/client';
import { useAuthStore } from '@features/auth/store/authStore';
import { useSupportHelp, type SupportStep } from './hooks/useSupportHelp';
import { SupportAssistantPanel } from './components/SupportAssistantPanel';
import { GuidedTour } from './components/GuidedTour';
import { SupportFeedbackToast } from './components/SupportFeedbackToast';

export interface ErrorDetail {
  code: string;
  message: string;
}

export interface ReportErrorArgs {
  title?: string;
  message: string;
  details?: ErrorDetail[];
  screen?: string; // pantalla/acción (ej. "crear cliente")
  /**
   * XML del documento, cuando el caso es sobre una factura concreta.
   *
   * El agente tiene un validador determinista (`validate_fe_xml`) que corre el
   * XSD oficial de la DGI: con el XML puede decir QUE CAMPO esta mal, en vez de
   * limitarse a interpretar el codigo de rechazo. Viaja dentro de `context`, que
   * el agente serializa hacia el modelo.
   */
  invoiceXml?: string;
  /**
   * Payload del documento (JSON con el detalle que el portal envio). Se usa
   * cuando la factura rechazada no llego a tener XML: sin el, el agente solo
   * cuenta con el codigo de rechazo y no puede señalar el campo.
   */
  invoicePayload?: unknown;
  /**
   * Respuesta CRUDA de la DGI (campo `response` del detalle). Es el unico lugar
   * donde viene `gResProcList`, la lista completa de hallazgos.
   *
   * Importa por como responde la DGI: `gResProc` (singular) es el veredicto que
   * decide, y `gResProcList` es el detalle, donde un mismo problema aparece
   * repetido item por item. Una factura puede traer 20 entradas que en realidad
   * son un unico error en 19 items mas el que bloquea. Sin esto el agente solo ve
   * el codigo principal y no puede agrupar ni distinguir rechazos de avisos.
   */
  dgiResponse?: string;
  /** Datos minimos de la factura para que el agente sepa de cual habla. */
  invoiceRef?: { number?: string; cufe?: string; status?: string };
}

interface Ctx {
  reportError: (args: ReportErrorArgs) => void;
  // Muestra el toast "¿te funcionó?" en el borde inferior (persiste tras cerrar el modal).
  showFeedbackToast: (interactionId: string, topic?: string) => void;
  // Feature flags del proyecto (los prende/apaga el equipo desde el portal admin):
  // master del asistente + botón "Guíame en pantalla".
  assistantEnabled: boolean;
  guideEnabled: boolean;
}

const SupportErrorContext = createContext<Ctx>({
  reportError: () => {},
  showFeedbackToast: () => {},
  assistantEnabled: false,
  guideEnabled: false,
});

/** Llamá esto desde el onError/catch de una acción para mostrar el error + Asistente IA. */
export function useReportError() {
  return useContext(SupportErrorContext).reportError;
}

/** Para disparar el toast de feedback desde otros componentes (ej. SupportReportButton). */
export function useShowFeedbackToast() {
  return useContext(SupportErrorContext).showFeedbackToast;
}

/** Flags de features del asistente (para gatear botones en el portal). */
export function useSupportConfig() {
  const { assistantEnabled, guideEnabled } = useContext(SupportErrorContext);
  return { assistantEnabled, guideEnabled };
}

/**
 * Provee un modal de error único con "Asistente IA" inline. Cualquier feature lo
 * dispara con `reportError({...})` desde su onError → el agente analiza el error y
 * sugiere la solución. Opt-in (no se activa en búsquedas/validaciones).
 *
 * FAIL-SAFE: los flags arrancan APAGADOS. Si el BFF no tiene configurado el
 * agente (o `/support/config` falla), nada del asistente aparece ni se llama, y
 * el portal se comporta exactamente como antes de esta feature.
 */
export function SupportErrorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReportErrorArgs & { open: boolean }>({ open: false, message: '' });
  // El tour vive acá (no dentro del modal) para que SOBREVIVA al cierre del modal.
  // Guardamos sessionId + goal para la guía iterativa (al cerrar el modal, el hook
  // se resetea, así que capturamos el contexto ACÁ).
  const [tour, setTour] = useState<{ steps: SupportStep[]; sessionId: string | null; goal: string } | null>(null);
  // Toast de feedback (vive acá → persiste aunque se cierre el modal).
  const [feedbackToast, setFeedbackToast] = useState<{ interactionId: string; topic?: string } | null>(null);

  // Feature flags del proyecto. Arrancan APAGADOS (fail-safe: nada aparece ni se
  // llama hasta confirmar que están activos). Se consultan al loguear.
  const { isAuthenticated } = useAuthStore();
  const [flags, setFlags] = useState({ assistant: false, guide: false });
  useEffect(() => {
    if (!isAuthenticated) {
      setFlags({ assistant: false, guide: false });
      return;
    }
    let alive = true;
    apiClient
      .get('/support/config')
      .then((r) => alive && setFlags({ assistant: r.data?.assistant_enabled !== false, guide: r.data?.guide_enabled !== false }))
      .catch(() => alive && setFlags({ assistant: false, guide: false }));
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  const reportError = useCallback((args: ReportErrorArgs) => {
    setState({ ...args, open: true });
  }, []);

  const showFeedbackToast = useCallback((interactionId: string, topic?: string) => {
    setFeedbackToast({ interactionId, topic });
  }, []);

  const close = () => setState((s) => ({ ...s, open: false }));

  // Al pedir guía: capturamos contexto, cerramos el modal y arrancamos el tour.
  const startGuide = (steps: SupportStep[]) => {
    setTour({ steps, sessionId: help.sessionId, goal: state.message });
    setState((s) => ({ ...s, open: false }));
  };

  const help = useSupportHelp({
    // Si el asistente está apagado, NO se llama al agente (enabled=false).
    enabled: state.open && !!state.message && flags.assistant,
    message: [state.message, ...(state.details || []).map((d) => `${d.code}: ${d.message}`)].join('\n'),
    errorCodes: (state.details || []).map((d) => d.code).filter(Boolean),
    screen: state.screen || state.title,
    invoiceXml: state.invoiceXml,
    invoicePayload: state.invoicePayload,
    dgiResponse: state.dgiResponse,
    invoiceRef: state.invoiceRef,
  });

  // Apenas el agente responde, mostramos el toast de feedback (persiste aunque se
  // cierre el modal).
  useEffect(() => {
    if (flags.assistant && help.interactionId) showFeedbackToast(help.interactionId);
  }, [flags.assistant, help.interactionId, showFeedbackToast]);

  return (
    <SupportErrorContext.Provider value={{ reportError, showFeedbackToast, assistantEnabled: flags.assistant, guideEnabled: flags.guide }}>
      {children}
      <Modal
        // Capa propia por encima de los modales normales: este modal se abre
        // SOBRE otros (ej. el de crear usuario cuando falla la creación), y con
        // los dos en `z-50` el clic que cerraba éste alcanzaba también al de
        // abajo, cerrando ambos de un golpe.
        layer={60}
        isOpen={state.open}
        onClose={close}
        kicker="ERROR"
        title={state.title || 'Ocurrió un error'}
        icon={<ExclamationTriangleIcon />}
        size="lg"
        footer={
          <div className="flex justify-end">
            <button type="button" onClick={close} className={CTA}>
              Cerrar
            </button>
          </div>
        }
      >
        <div className="space-y-4" data-support-ui>
          <p className="text-[14px] leading-[1.55] text-secondary-600 dark:text-secondary-300">{state.message}</p>

          {state.details && state.details.length > 0 && (
            <div className="space-y-2">
              {state.details.map((d, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 rounded-xl border border-danger-200 bg-danger-50 p-3 dark:border-danger-800 dark:bg-danger-900/20"
                >
                  {d.code && (
                    <span className="mt-px inline-flex h-5 shrink-0 items-center justify-center rounded-full bg-danger-100 px-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-danger-800 dark:bg-danger-900/40 dark:text-danger-200">
                      {d.code}
                    </span>
                  )}
                  <p className="flex-1 text-[13.5px] leading-[1.5] text-danger-800 dark:text-danger-200">{d.message}</p>
                </div>
              ))}
            </div>
          )}

          {flags.assistant && <SupportAssistantPanel help={help} onGuide={startGuide} guideEnabled={flags.guide} />}
        </div>
      </Modal>

      {/* Recorrido guiado sobre el portal real (fuera del modal, para que persista). */}
      {tour && tour.steps.length > 0 && (
        <GuidedTour
          steps={tour.steps}
          sessionId={tour.sessionId ?? undefined}
          goal={tour.goal}
          onComplete={(journey) =>
            apiClient
              .post('/support/guide-complete', { sessionId: tour.sessionId, goal: tour.goal, journey })
              .catch(() => {})
          }
          onClose={() => setTour(null)}
        />
      )}

      {/* Toast "¿te funcionó?" (persiste tras cerrar el modal). */}
      {feedbackToast && (
        <SupportFeedbackToast
          interactionId={feedbackToast.interactionId}
          topic={feedbackToast.topic}
          onClose={() => setFeedbackToast(null)}
        />
      )}
    </SupportErrorContext.Provider>
  );
}
